import { useEffect, useRef, useState } from "react"
import { Answers, isFormAnswer, Question } from "@/types/agent"

import type {
  ClarifyingQuestionPresentation,
  EventClarifyingQuestionsBaseProperties,
} from "@/lib/analytics/amplitude-types"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { useTranslation } from "@/lib/i18n/i18n-context"
import { useAppDispatch } from "@/lib/storage/hooks"
import { sendMessage } from "@/lib/storage/message-thunks"
import { formatAnswerValue } from "./agent-question-event-display"
import { AgentQuestionForm } from "./agent-question-form"

export const CLARIFYING_QUESTIONS_SKIP_REPLY =
  "Continue with the suggested defaults."

// Form answers are keyed by field id; surface them as label/value pairs
// (e.g. "First name" / "Jane") in the order the form's rows declare.
function formAnswerFields(
  question: Question,
  value: Answers[string]
): { label: string; value: string }[] {
  if (question.input_type !== "form" || !isFormAnswer(value)) return []

  const fields: { label: string; value: string }[] = []
  for (const row of question.config?.rows ?? []) {
    for (const field of row.fields) {
      const fieldValue = value[field.id]
      if (typeof fieldValue === "string" && fieldValue.trim().length > 0) {
        fields.push({ label: field.label, value: fieldValue.trim() })
      }
    }
  }
  return fields
}

export function answersToMessage(
  questions: Question[],
  answers: Answers,
  skippedQuestionIds: string[] = [],
  skippedLabel = "Skipped"
): string {
  return questions
    .map((q) => {
      const hasAnswer = answers[q.id] !== undefined
      const isSkipped = skippedQuestionIds.includes(q.id)
      if (!hasAnswer && !isSkipped) return ""

      if (hasAnswer) {
        // One "Label: value" line per form field so the reply reconstructs
        // into one display item per field (see getClarifyingAnswerItems).
        const formFields = formAnswerFields(q, answers[q.id])
        if (formFields.length > 0) {
          return formFields.map((f) => `${f.label}: ${f.value}`).join("\n")
        }
      }

      const value = hasAnswer ? formatAnswerValue(answers[q.id]) : skippedLabel
      const label = q.title || q.question_text
      return label ? `${label}: ${value}` : value
    })
    .filter((s) => s.length > 0)
    .join("\n")
}

function formatAnswerItem(question: Question, value: Answers[string]): string {
  const formattedValue = formatAnswerValue(value)
  const label = question.title || question.question_text
  return label ? `${label}\n${formattedValue}` : formattedValue
}

function formatSkippedAnswerItem(question: Question): string {
  const label = question.title || question.question_text
  return label ? `${label}\nSkipped` : "Skipped"
}

function splitReplyBodyParts(replyBody: string): string[] {
  // Prefer paragraph / line breaks from the current format; fall back to the
  // legacy semicolon-separated format for older messages.
  if (replyBody.includes("\n")) {
    return replyBody
      .split(/\n+/)
      .map((part) => part.trim())
      .filter((s) => s.length > 0)
  }

  return replyBody
    .split("; ")
    .map((part) => part.trim())
    .filter((s) => s.length > 0)
}

function formatReplyBodyPart(questions: Question[], part: string): string {
  const trimmedPart = part.trim()
  const colonIdx = trimmedPart.indexOf(": ")
  if (colonIdx === -1) return trimmedPart

  const label = trimmedPart.slice(0, colonIdx)
  const value = trimmedPart.slice(colonIdx + 2)
  const question = questions.find(
    (q) => q.title === label || q.question_text === label
  )
  if (!question) return `${label}\n${value}`

  const displayLabel = question.title || question.question_text
  return displayLabel ? `${displayLabel}\n${value}` : value
}

export function getClarifyingAnswerItems(
  questions: Question[],
  options: {
    answers?: Answers
    replyBody?: string
    skippedQuestionIds?: string[]
  }
): string[] {
  const { answers, replyBody, skippedQuestionIds = [] } = options

  if (answers) {
    return questions
      .flatMap((q) => {
        if (answers[q.id] !== undefined) {
          const formFields = formAnswerFields(q, answers[q.id])
          if (formFields.length > 0) {
            return formFields.map((f) => `${f.label}\n${f.value}`)
          }
          return [formatAnswerItem(q, answers[q.id])]
        }
        if (skippedQuestionIds.includes(q.id)) {
          return [formatSkippedAnswerItem(q)]
        }
        return []
      })
      .filter((s) => s.length > 0)
  }

  if (replyBody) {
    if (replyBody === CLARIFYING_QUESTIONS_SKIP_REPLY) {
      return [replyBody]
    }

    return splitReplyBodyParts(replyBody)
      .map((part) => formatReplyBodyPart(questions, part))
      .filter((s) => s.length > 0)
  }

  return []
}

export function ClarifyingAnswersDisplay({ items }: { items: string[] }) {
  if (items.length === 0) return null

  const showLabels = items.length > 1

  return (
    <div className="prose prose-chat">
      <ul className="m-0 list-none space-y-[1em] p-0">
        {items.map((item, index) => {
          const [label, ...valueParts] = item.split("\n")
          const value = valueParts.join("\n")
          const displayText = value || item

          return (
            <li key={index}>
              {showLabels && value ? (
                <>
                  <div className="text-xs font-normal text-muted">{label}</div>
                  <div className="font-medium text-pretty">{value}</div>
                </>
              ) : (
                <div className="font-medium text-pretty">{displayText}</div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function getClarifyingQuestionMode(questions: Question[]): {
  presentation: ClarifyingQuestionPresentation
  isChatAnswerable: boolean
  singleSelectInstantSubmit: boolean
  otpInstantSubmit: boolean
} {
  const isChatAnswerable =
    questions.length === 1 && questions[0].input_type === "text"

  const singleSelectInstantSubmit =
    questions.length === 1 &&
    (questions[0].input_type === "single_select" ||
      questions[0].input_type === "toggle" ||
      questions[0].input_type === "dropdown")

  // Sole OTP question: hide Finish and submit as soon as all digits are filled.
  const otpInstantSubmit =
    questions.length === 1 && questions[0].input_type === "otp"

  let presentation: ClarifyingQuestionPresentation = "form"
  if (isChatAnswerable) {
    presentation = "chat_answerable"
  } else if (singleSelectInstantSubmit || otpInstantSubmit) {
    presentation = "instant_submit"
  }

  return {
    presentation,
    isChatAnswerable,
    singleSelectInstantSubmit,
    otpInstantSubmit,
  }
}

function buildClarifyingQuestionBaseProps(
  messageId: number,
  questions: Question[],
  presentation: ClarifyingQuestionPresentation
): EventClarifyingQuestionsBaseProperties {
  return {
    messageId,
    questionCount: questions.length,
    inputTypes: questions.map((q) => q.input_type),
    presentation,
  }
}

function clarifyingAnswersUsedOther(
  questions: Question[],
  answers: Answers
): boolean {
  return questions.some((q) => {
    const value = answers[q.id]
    if (value === undefined) return false

    if (typeof value === "string") {
      return value.length > 0 && !q.options.includes(value)
    }
    if (Array.isArray(value)) {
      return value.some((item) => !q.options.includes(item))
    }
    return false
  })
}

function ClarifyingQuestionsSummary({ questions }: { questions: Question[] }) {
  // A form's fields reappear labeled in the customer's reply, so it needs no
  // answered-state summary line.
  const items = questions
    .filter((q) => q.input_type !== "form")
    .map((q) => q.question_text || q.title || "")
    .filter((s) => s.length > 0)

  if (items.length === 0) return null

  return (
    <div className="prose prose-chat mt-[1em]">
      {items.length > 1 ? (
        <ol>
          {items.map((item, index) => (
            <li key={index} className="text-pretty">
              {item}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-pretty">{items[0]}</p>
      )}
    </div>
  )
}

type LocalReply = {
  type: "answers"
  answers: Answers
  skippedQuestionIds?: string[]
}

export function ClarifyingQuestionMessage({
  messageId,
  questions,
  replyBody,
  readOnly,
}: {
  messageId: number
  questions: Question[]
  replyBody?: string
  readOnly?: boolean
}) {
  const dispatch = useAppDispatch()
  const t = useTranslation()
  const [localReply, setLocalReply] = useState<LocalReply | null>(null)
  const viewedRef = useRef(false)

  const isAnswered = localReply !== null || !!replyBody
  const questionMode = getClarifyingQuestionMode(questions)
  const baseAnalyticsProps = buildClarifyingQuestionBaseProps(
    messageId,
    questions,
    questionMode.presentation
  )

  useEffect(() => {
    if (readOnly || replyBody || localReply || viewedRef.current) return
    viewedRef.current = true
    sendAnalyticsEvent({
      name: "ViewClarifyingQuestions",
      properties: baseAnalyticsProps,
    })
  }, [baseAnalyticsProps, readOnly, replyBody, localReply])

  const handleSubmit = (
    answers: Answers,
    skippedQuestionIds: string[] = []
  ) => {
    const messageString = answersToMessage(
      questions,
      answers,
      skippedQuestionIds,
      t("answerSkipped")
    )
    if (messageString.length === 0) return
    sendAnalyticsEvent({
      name: "SubmitClarifyingQuestions",
      properties: {
        ...baseAnalyticsProps,
        answeredCount: Object.keys(answers).length,
        usedOther: clarifyingAnswersUsedOther(questions, answers),
      },
    })
    setLocalReply({ type: "answers", answers, skippedQuestionIds })
    void dispatch(sendMessage({ messageString, subType: "clarifying-answer" }))
  }

  if (isAnswered || readOnly) {
    return <ClarifyingQuestionsSummary questions={questions} />
  }

  if (questionMode.isChatAnswerable) {
    const questionText = questions[0].question_text?.trim()
    if (!questionText) return null

    return (
      <div className="prose prose-chat mt-[1em]">
        <p className="text-pretty">{questionText}</p>
      </div>
    )
  }

  return (
    <AgentQuestionForm
      questions={questions}
      onSubmit={handleSubmit}
      singleSelectInstantSubmit={questionMode.singleSelectInstantSubmit}
      otpInstantSubmit={questionMode.otpInstantSubmit}
      analyticsEnabled
      messageId={messageId}
    />
  )
}
